import { useEffect, useState } from 'react';
import { 
  Truck, Package, DollarSign, TrendingUp, TrendingDown, Clock, 
  AlertTriangle, CheckCircle, Users, MapPin, Calendar, Activity
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { LiveTruckMap } from './LiveTruckMap';
import { getVehicleLocations } from '../../services/locationService';
import { calculateDistance, calculateETA, formatETA, approximateGeocode } from '../../utils/gpsEta';

export function AdvancedDashboard() {
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'Admin' || profile?.role === 'Superadmin';
  
  const [stats, setStats] = useState({
    activeLoads: 0,
    completedToday: 0,
    revenue: 0,
    revenueChange: 0,
    activeDrivers: 0,
    availableDrivers: 0,
    activeTrucks: 0,
    maintenanceDue: 0,
    pendingDispatch: 0,
    lateDeliveries: 0,
  });

  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [upcomingDeliveries, setUpcomingDeliveries] = useState<any[]>([]);
  const [companyName, setCompanyName] = useState<string>('');
  const [companyInfo, setCompanyInfo] = useState<{ dot?: string; mc?: string }>({});

  useEffect(() => {
    loadDashboardData();
    loadCompanyInfo();
    
    // Auto-refresh every 30 seconds to catch new revenue records
    const interval = setInterval(() => {
      loadDashboardData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  async function loadCompanyInfo() {
    try {
      const { data } = await supabase
        .from('company_settings')
        .select('company_name, dot_number, mc_number')
        .limit(1)
        .maybeSingle();
      
      if (data) {
        setCompanyName(data.company_name || 'TMS Dashboard');
        setCompanyInfo({
          dot: data.dot_number,
          mc: data.mc_number
        });
      }
    } catch (error) {
      console.error('Error loading company info:', error);
    }
  }

  async function loadDashboardData() {
    try {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const [loads, drivers, trucks, dispatches, truckLocations, profilesRes] = await Promise.all([
        supabase.from('loads').select(`
          *,
          load_stops(stop_type, stop_order, city, state, date)
        `).order('created_at', { ascending: false }),
        supabase.from('drivers').select('*'),
        supabase.from('trucks').select('*'),
        supabase.from('dispatches').select(`
          *,
          loads!dispatches_load_id_fkey(
            load_number,
            status,
            delivery_date,
            delivery_city,
            delivery_state,
            load_stops(stop_type, stop_order, city, state, date)
          ),
          drivers!dispatches_driver_id_fkey(first_name, last_name, dispatcher_id),
          trucks!dispatches_truck_id_fkey(unit_number, samsara_vehicle_id, samsara_id, dispatcher_id)
        `).in('status', ['dispatched', 'active']).order('created_at', { ascending: false }),
        getVehicleLocations(),
        supabase.from('profiles').select('id, full_name')
      ]);

      if (loads.error) {
        console.error('Dashboard: Error loading loads:', loads.error);
      }
      if (dispatches.error) {
        console.error('Dashboard: Error loading dispatches:', dispatches.error);
      }
      console.log('Dashboard: Loads fetched:', loads.data?.length || 0, 'loads');
      console.log('Dashboard: Active dispatches fetched:', dispatches.data?.length || 0);

      // A load is "removed" if its status is 'removed' OR (legacy convention)
      // its load_number was suffixed with _REMOVED_. Removed loads must never be
      // counted or shown anywhere on the dashboard.
      const isRemovedLoad = (l: any) =>
        !l ||
        l.status?.toLowerCase() === 'removed' ||
        (l.load_number || '').includes('_REMOVED_');

      // All loads except removed — every count/list below works off this.
      const visibleLoads = loads.data?.filter(l => !isRemovedLoad(l)) || [];

      const activeLoads = visibleLoads.filter(l => {
        const status = l.status?.toLowerCase();
        return status === 'dispatched' ||
               status === 'in_transit' ||
               status === 'en_route';
      });
      const completedToday = visibleLoads.filter(l => {
        return l.status?.toLowerCase() === 'delivered' && new Date(l.delivery_date).toDateString() === today.toDateString();
      });

      // Get actual completed revenue from revenue_records table
      const { data: revenueRecords } = await supabase
        .from('revenue_records')
        .select('total_revenue, completed_date');

      console.log('Dashboard: Revenue records fetched:', revenueRecords?.length || 0, revenueRecords);

      // Calculate revenue for this month and last month from completed loads
      const thisMonthRevenue = revenueRecords?.filter(r => new Date(r.completed_date) >= thisMonth)
        .reduce((sum, r) => sum + (Number(r.total_revenue) || 0), 0) || 0;
      const lastMonthRevenue = revenueRecords?.filter(r => {
        const date = new Date(r.completed_date);
        return date >= lastMonth && date < thisMonth;
      }).reduce((sum, r) => sum + (Number(r.total_revenue) || 0), 0) || 0;
      
      console.log('Dashboard: This month revenue:', thisMonthRevenue, 'Last month:', lastMonthRevenue);
      
      const revenueChange = lastMonthRevenue > 0 
        ? ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue * 100)
        : 0;

      // Count only active drivers (driver_status = 'active')
      const activeDriversList = drivers.data?.filter(d => d.driver_status === 'active') || [];
      // Count trucks with truck_status = 'ACTIVE' and have a company_id (from the 3 companies)
      const activeTrucksList = trucks.data?.filter(t => 
        t.truck_status === 'ACTIVE' && t.company_id
      ) || [];
      
      // Calculate maintenance due (trucks with next_maintenance_date in the past or within 7 days)
      const maintenanceDueList = trucks.data?.filter(t => {
        if (!t.next_maintenance_date) return false;
        const maintenanceDate = new Date(t.next_maintenance_date);
        const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        return maintenanceDate <= sevenDaysFromNow;
      }) || [];

      // Calculate late deliveries (expected delivery date passed but not delivered)
      const lateDeliveriesList = visibleLoads.filter(l => {
        const status = l.status?.toLowerCase();
        if (status === 'delivered' || status === 'cancelled') return false;
        if (!l.delivery_date) return false;
        return new Date(l.delivery_date) < now;
      });

      setStats({
        activeLoads: activeLoads.length,
        completedToday: completedToday.length,
        revenue: thisMonthRevenue,
        revenueChange: Math.round(revenueChange * 10) / 10,
        activeDrivers: activeDriversList.length,
        availableDrivers: Math.max(0, activeDriversList.length - activeLoads.length),
        activeTrucks: activeTrucksList.length,
        maintenanceDue: maintenanceDueList.length,
        pendingDispatch: visibleLoads.filter(l => l.status?.toLowerCase() === 'booked').length,
        lateDeliveries: lateDeliveriesList.length,
      });

      // Real recent activity from loads (removed loads excluded via visibleLoads)
      const recentLoadsActivity = visibleLoads.slice(0, 10).map(load => {
        const createdAt = new Date(load.created_at);
        const minutesAgo = Math.floor((now.getTime() - createdAt.getTime()) / 60000);
        const timeAgo = minutesAgo < 60 
          ? `${minutesAgo} min ago`
          : minutesAgo < 1440
          ? `${Math.floor(minutesAgo / 60)} hour${Math.floor(minutesAgo / 60) > 1 ? 's' : ''} ago`
          : `${Math.floor(minutesAgo / 1440)} day${Math.floor(minutesAgo / 1440) > 1 ? 's' : ''} ago`;

        const loadStatus = load.status?.toLowerCase();
        if (loadStatus === 'delivered') {
          return {
            type: 'load_completed',
            message: `Load ${load.load_number} completed`,
            time: timeAgo,
            icon: CheckCircle,
            color: 'text-green-600'
          };
        } else if (loadStatus === 'dispatched' || loadStatus === 'en_route' || loadStatus === 'in_transit') {
          const driverName = load.driver?.name || 'driver';
          return {
            type: 'dispatch',
            message: `Load ${load.load_number} dispatched to ${driverName}`,
            time: timeAgo,
            icon: Truck,
            color: 'text-blue-600'
          };
        } else {
          return {
            type: 'load_created',
            message: `Load ${load.load_number} created`,
            time: timeAgo,
            icon: Package,
            color: 'text-slate-600'
          };
        }
      }).filter(Boolean) || [];

      // Add maintenance alerts
      const maintenanceActivity = maintenanceDueList.slice(0, 2).map(truck => ({
        type: 'alert',
        message: `Truck ${truck.unit_number} maintenance due`,
        time: 'Soon',
        icon: AlertTriangle,
        color: 'text-orange-600'
      }));

      setRecentActivity([...maintenanceActivity, ...recentLoadsActivity].slice(0, 5));

      // Build truck location map from Samsara data
      const truckLocationMap = new Map();
      if (truckLocations.success && truckLocations.locations) {
        truckLocations.locations.forEach((loc: any) => {
          truckLocationMap.set(loc.vehicleId, {
            latitude: loc.latitude,
            longitude: loc.longitude,
            time: loc.time
          });
        });
      }

      // Real upcoming deliveries — show loads that are still in progress
      // (dispatched / not yet delivered and not archived). We key off the LOAD's
      // status, not the dispatch's: a load can be delivered/removed at the load
      // level while its dispatch row stays 'active', which is how stale
      // "Overdue 1337h" entries leak in. Anything delivered/cancelled/archived/
      // removed is excluded.
      const finishedLoadStatuses = ['delivered', 'cancelled', 'canceled', 'archived', 'removed', 'completed'];

      // Resolve a dispatch's delivery date (main table, else the LAST stop).
      const getDispatchDeliveryDate = (d: any): number => {
        let dateStr = d.loads?.delivery_date;
        if (!dateStr && d.loads?.load_stops?.length > 0) {
          const sorted = [...d.loads.load_stops].sort((x: any, y: any) => x.stop_order - y.stop_order);
          dateStr = sorted[sorted.length - 1]?.date;
        }
        return dateStr ? new Date(dateStr).getTime() : Infinity;
      };

      // Map dispatcher_id -> name so we can show who the dispatcher is.
      const dispatcherNameMap = new Map<string, string>();
      (profilesRes.data || []).forEach((p: any) => {
        if (p.id) dispatcherNameMap.set(p.id, p.full_name || 'Unknown');
      });

      // "Upcoming" = deliveries due from the start of today onward. This drops
      // both ancient stale loads (the "Overdue 1300h" entries) and merely past
      // deliveries, so the section shows what's actually coming up next.
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

      const upcomingDispatches = dispatches.data?.filter(d => {
        if (!d.loads) return false;
        if (isRemovedLoad(d.loads)) {
          console.log('Dashboard: Filtering out removed load:', d.loads.load_number);
          return false;
        }
        const loadStatus = (d.loads.status || '').toLowerCase();
        if (finishedLoadStatuses.includes(loadStatus)) {
          return false;
        }
        // Only deliveries due today or later — no past/overdue clutter.
        if (getDispatchDeliveryDate(d) < startOfToday) {
          return false;
        }
        return true;
      }) || [];
      
      const upcoming = upcomingDispatches
        .sort((a, b) => {
          // Get delivery date from main table or load_stops (use LAST stop)
          const getDeliveryDate = (dispatch: any) => {
            let dateStr = dispatch.loads?.delivery_date;
            if (!dateStr && dispatch.loads?.load_stops && dispatch.loads.load_stops.length > 0) {
              // Sort by stop_order and get the LAST stop (highest stop_order)
              const sortedStops = [...dispatch.loads.load_stops].sort((x: any, y: any) => x.stop_order - y.stop_order);
              const lastStop = sortedStops[sortedStops.length - 1];
              dateStr = lastStop.date;
            }
            return dateStr ? new Date(dateStr).getTime() : Infinity;
          };
          
          return getDeliveryDate(a) - getDeliveryDate(b);
        })
        .slice(0, 5)
        .map(dispatch => {
          let eta = 'TBD';
          let isLate = false;
          let deliveryDateStr = dispatch.loads?.delivery_date;
          
          // If no delivery_date in main table, get from LAST stop in load_stops
          if (!deliveryDateStr && dispatch.loads?.load_stops && dispatch.loads.load_stops.length > 0) {
            const sortedStops = [...dispatch.loads.load_stops].sort((a: any, b: any) => a.stop_order - b.stop_order);
            const lastStop = sortedStops[sortedStops.length - 1];
            deliveryDateStr = lastStop.date;
          }
          
          // Try GPS-based ETA first if truck has location
          let gpsEtaCalculated = false;
          if (dispatch.trucks) {
            const vehicleId = dispatch.trucks.samsara_vehicle_id || dispatch.trucks.samsara_id;
            const truckLocation = truckLocationMap.get(vehicleId);
            
            if (truckLocation && truckLocation.latitude && truckLocation.longitude) {
              // Get delivery location from last stop
              let deliveryCity = dispatch.loads.delivery_city;
              let deliveryState = dispatch.loads.delivery_state;
              
              if ((!deliveryCity || !deliveryState || deliveryCity === 'TBD' || deliveryState === 'TBD')) {
                if (dispatch.loads?.load_stops && dispatch.loads.load_stops.length > 0) {
                  const sortedStops = [...dispatch.loads.load_stops].sort((a: any, b: any) => a.stop_order - b.stop_order);
                  const lastStop = sortedStops[sortedStops.length - 1];
                  deliveryCity = lastStop.city;
                  deliveryState = lastStop.state;
                }
              }
              
              // Try to geocode delivery location
              if (deliveryCity && deliveryState) {
                const deliveryCoords = approximateGeocode(deliveryCity, deliveryState);
                
                if (deliveryCoords) {
                  // Calculate distance and ETA
                  const distanceMiles = calculateDistance(
                    truckLocation.latitude,
                    truckLocation.longitude,
                    deliveryCoords.lat,
                    deliveryCoords.lng
                  );
                  
                  const etaHours = calculateETA(distanceMiles, 55); // 55mph average
                  eta = `${formatETA(etaHours)} (${distanceMiles}mi)`;
                  gpsEtaCalculated = true;
                  
                  // Check if overdue based on scheduled date
                  if (deliveryDateStr) {
                    const deliveryDate = new Date(deliveryDateStr);
                    isLate = deliveryDate < now;
                  }
                }
              }
            }
          }
          
          // Fall back to schedule-based ETA if GPS ETA not available
          if (!gpsEtaCalculated && deliveryDateStr) {
            const deliveryDate = new Date(deliveryDateStr);
            const hoursUntil = Math.floor((deliveryDate.getTime() - now.getTime()) / (1000 * 60 * 60));
            isLate = deliveryDate < now;
            
            if (isLate) {
              const hoursOverdue = Math.abs(hoursUntil);
              eta = `Overdue ${hoursOverdue}h`;
            } else if (hoursUntil < 24) {
              eta = `${hoursUntil} hour${hoursUntil !== 1 ? 's' : ''}`;
            } else {
              const daysUntil = Math.floor(hoursUntil / 24);
              eta = `${daysUntil} day${daysUntil !== 1 ? 's' : ''}`;
            }
          }

          // Get delivery location from loads table or LAST stop
          let deliveryCity = dispatch.loads.delivery_city;
          let deliveryState = dispatch.loads.delivery_state;
          
          // If TBD or missing, get from LAST stop in load_stops
          if ((!deliveryCity || !deliveryState || deliveryCity === 'TBD' || deliveryState === 'TBD')) {
            if (dispatch.loads?.load_stops && dispatch.loads.load_stops.length > 0) {
              const sortedStops = [...dispatch.loads.load_stops].sort((a: any, b: any) => a.stop_order - b.stop_order);
              const lastStop = sortedStops[sortedStops.length - 1];
              deliveryCity = lastStop.city;
              deliveryState = lastStop.state;
            }
          }

          const driverName = dispatch.drivers
            ? `${dispatch.drivers.first_name} ${dispatch.drivers.last_name}`
            : 'Unassigned';

          const truckNumber = dispatch.trucks?.unit_number || 'Unassigned';

          // Dispatcher: prefer the truck's dispatcher, fall back to the driver's.
          const dispatcherId = dispatch.trucks?.dispatcher_id || dispatch.drivers?.dispatcher_id;
          const dispatcherName = dispatcherId
            ? (dispatcherNameMap.get(dispatcherId) || 'Unknown')
            : 'Unassigned';

          return {
            load: dispatch.loads.load_number,
            driver: driverName,
            truck: truckNumber,
            dispatcher: dispatcherName,
            destination: deliveryCity && deliveryState
              ? `${deliveryCity}, ${deliveryState}`
              : 'Unknown',
            eta,
            status: isLate ? 'delayed' : 'on-time'
          };
        });

      setUpcomingDeliveries(upcoming);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
  }

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              Welcome back, {profile?.full_name || profile?.email?.split('@')[0] || 'User'}
            </h1>
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <p className="text-slate-700 font-medium">
                  {companyName}
                </p>
              </div>
              {profile?.role && (
                <>
                  <span className="text-slate-300">•</span>
                  <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full font-semibold text-sm">
                    {profile.role}
                  </span>
                </>
              )}
              <span className="text-slate-300">•</span>
              <p className="text-slate-600">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              {(companyInfo.dot || companyInfo.mc) && (
                <>
                  <span className="text-slate-300">•</span>
                  <div className="flex items-center gap-3 text-sm">
                    {companyInfo.dot && (
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-semibold">
                        DOT: {companyInfo.dot}
                      </span>
                    )}
                    {companyInfo.mc && (
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-semibold">
                        MC: {companyInfo.mc}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right bg-blue-50 px-4 py-3 rounded-lg border border-blue-100">
              <p className="text-xs text-blue-600 font-semibold">Current Time</p>
              <p className="text-xl font-bold text-blue-900">
                {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            <div className="text-right bg-indigo-50 px-4 py-3 rounded-lg border border-indigo-100">
              <p className="text-xs text-indigo-600 font-semibold">Active Loads</p>
              <p className="text-xl font-bold text-indigo-900">{stats.activeLoads}</p>
            </div>
            {isAdmin && (
              <div className="text-right bg-green-50 px-4 py-3 rounded-lg border border-green-100">
                <p className="text-xs text-green-600 font-semibold">Revenue MTD</p>
                <p className="text-xl font-bold text-green-900">${(stats.revenue / 1000).toFixed(1)}K</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Live Truck Map */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Live Fleet Tracking</h2>
        <LiveTruckMap />
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-4 gap-4">
        {/* Active Loads */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="bg-blue-500 p-2.5 rounded-lg">
              <Package className="w-5 h-5 text-white" />
            </div>
            <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded">ACTIVE</span>
          </div>
          <p className="text-3xl font-bold text-slate-900 mb-1">{stats.activeLoads}</p>
          <p className="text-sm text-slate-600 mb-3">Active Loads</p>
          <div className="flex items-center gap-1.5 text-xs text-slate-600 bg-slate-50 px-2 py-1.5 rounded">
            <TrendingUp className="w-3.5 h-3.5" />
            <span>{stats.completedToday} completed today</span>
          </div>
        </div>

        {/* Revenue - Admin Only */}
        {isAdmin && (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="bg-green-500 p-2.5 rounded-lg">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded">
                +{stats.revenueChange}%
              </span>
            </div>
            <p className="text-3xl font-bold text-slate-900 mb-1">${(stats.revenue / 1000).toFixed(1)}K</p>
            <p className="text-sm text-slate-600 mb-3">Total Revenue</p>
            <div className="flex items-center gap-1.5 text-xs text-slate-600 bg-slate-50 px-2 py-1.5 rounded">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>+{stats.revenueChange}% vs last month</span>
            </div>
          </div>
        )}

        {/* Drivers */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="bg-purple-500 p-2.5 rounded-lg">
              <Users className="w-5 h-5 text-white" />
            </div>
            <span className="text-xs font-semibold text-purple-600 bg-purple-50 px-2 py-1 rounded">DRIVERS</span>
          </div>
          <p className="text-3xl font-bold text-slate-900 mb-1">{stats.activeDrivers}</p>
          <p className="text-sm text-slate-600 mb-3">Active Drivers</p>
          <div className="flex items-center gap-1.5 text-xs text-slate-600 bg-slate-50 px-2 py-1.5 rounded">
            <CheckCircle className="w-3.5 h-3.5" />
            <span>{stats.availableDrivers} available now</span>
          </div>
        </div>

        {/* Trucks */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="bg-orange-500 p-2.5 rounded-lg">
              <Truck className="w-5 h-5 text-white" />
            </div>
            {stats.maintenanceDue > 0 && (
              <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded">
                {stats.maintenanceDue} DUE
              </span>
            )}
          </div>
          <p className="text-3xl font-bold text-slate-900 mb-1">{stats.activeTrucks}</p>
          <p className="text-sm text-slate-600 mb-3">Active Trucks</p>
          <div className="flex items-center gap-1.5 text-xs text-slate-600 bg-slate-50 px-2 py-1.5 rounded">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>{stats.maintenanceDue} maintenance due</span>
          </div>
        </div>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 flex items-center gap-3">
          <div className="bg-yellow-500 p-3 rounded-lg">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{stats.pendingDispatch}</p>
            <p className="text-xs text-slate-600">Pending Dispatch</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 flex items-center gap-3">
          <div className="bg-red-500 p-3 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{stats.lateDeliveries}</p>
            <p className="text-xs text-slate-600">Late Deliveries</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 flex items-center gap-3">
          <div className="bg-cyan-500 p-3 rounded-lg">
            <Calendar className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{stats.completedToday}</p>
            <p className="text-xs text-slate-600">Completed Today</p>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-3 gap-4">
        {/* Upcoming Deliveries - 2 columns */}
        <div className="col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Upcoming Deliveries
            </h3>
            <button className="text-sm text-blue-600 hover:text-blue-700 font-semibold">View All →</button>
          </div>
          <div className="space-y-2">
            {upcomingDeliveries.map((delivery, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-500 p-2 rounded-lg">
                    <Package className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">{delivery.load}</p>
                    <p className="text-xs text-slate-600">{delivery.driver} → {delivery.destination}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Truck {delivery.truck} · Dispatcher: {delivery.dispatcher}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-slate-900">ETA: {delivery.eta}</p>
                  <span className={`text-xs px-2 py-1 rounded font-semibold ${
                    delivery.status === 'on-time' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {delivery.status === 'on-time' ? 'On Time' : 'Delayed'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity - 1 column */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5 text-purple-600" />
            Recent Activity
          </h3>
          <div className="space-y-2">
            {recentActivity.map((activity, idx) => {
              const Icon = activity.icon;
              return (
                <div key={idx} className="flex items-start gap-2 p-2 rounded-lg bg-slate-50">
                  <div className={`${activity.color} p-1.5 rounded`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">{activity.message}</p>
                    <p className="text-xs text-slate-500">{activity.time}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
